import React, { useState } from 'react';
import { usePrograms } from '../../hooks/usePrograms';
import { BellIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: programs } = usePrograms();

  // Calculate programs that need setup
  const needsSetupPrograms = React.useMemo(() => {
    if (!programs || !Array.isArray(programs)) return [];

    return programs.filter(program => {
      const isIncomplete = !program.descriptionCustomer || !program.conditions || !program.cashbackRate || !program.countries?.length;
      return program.affiliateUrl && isIncomplete;
    });
  }, [programs]);

  const needsSetupCount = needsSetupPrograms.length;

  if (needsSetupCount === 0) {
    return null; // Don't show notification if no programs need setup
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      >
        <BellIcon className="h-6 w-6" />
        {needsSetupCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {needsSetupCount > 99 ? '99+' : needsSetupCount}
          </span>
        )}
      </button>

      {/* Notification dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Programs Need Setup
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {needsSetupCount} program{needsSetupCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {needsSetupPrograms.slice(0, 5).map((program) => (
              <div
                key={program._id}
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                      <ExclamationTriangleIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {program.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {program.affiliateUrl}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {!program.cashbackRate && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                          No cashback rate
                        </span>
                      )}
                      {!program.countries?.length && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                          No countries
                        </span>
                      )}
                      {!program.descriptionCustomer && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                          No description
                        </span>
                      )}
                      {!program.conditions && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">
                          No conditions
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {needsSetupCount > 5 && (
              <div className="px-4 py-2 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  And {needsSetupCount - 5} more program{needsSetupCount - 5 !== 1 ? 's' : ''} need setup
                </p>
              </div>
            )}
          </div>

          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setIsOpen(false);
                // Navigate to programs page
                window.location.href = '/programs';
              }}
              className="w-full text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              View all programs
            </button>
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown when clicking outside */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationDropdown; 